import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import BtcLogo from "../components/BtcLogo";

/**
 * BTC Email · Batch 2 — password-reset request page.
 *
 * The server response is intentionally non-enumerating: known and
 * unknown email addresses yield the same status.  The UI copy must
 * therefore also be non-enumerating.  No "we sent you an email"
 * promise is made; the message states what will happen *if* an
 * account exists.
 */
export default function ForgotPassword() {
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | submitted | error
  const [errorMsg, setErrorMsg] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!r.ok) throw new Error("request_failed");
      setStatus("submitted");
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again in a moment.");
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center px-4">
      <div
        className="bg-white rounded-3xl border border-gray-100 shadow-sm w-full max-w-md p-8 lg:p-10"
        data-testid="forgot-password-page"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <BtcLogo className="h-12 w-auto" />
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B] mb-2">
          Reset your password
        </h1>
        <p className="text-sm text-slate-600 mb-6">
          Enter the email address associated with your BTC Portal account and
          we will send instructions for choosing a new password.
        </p>

        {status === "submitted" ? (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-5"
            role="status"
            data-testid="forgot-password-submitted"
          >
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-slate-500 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-slate-700">
                <div className="font-semibold text-slate-900 mb-1">
                  Check your inbox soon.
                </div>
                <p>
                  If this address matches a BTC Portal account, a secure reset
                  link will arrive shortly. The link is valid for 30 minutes.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="forgot-password-form"
            action="/api/auth/forgot-password"
            className="space-y-4"
          >
            <label className="block">
              <span className="text-sm font-medium text-slate-800">
                Email address
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 focus:border-[#73B726] focus:outline-none focus:ring-2 focus:ring-[#73B726]/30"
                data-testid="forgot-password-email-input"
              />
            </label>
            {status === "error" && (
              <p
                className="text-sm text-red-600"
                role="alert"
                data-testid="forgot-password-error"
              >
                {errorMsg}
              </p>
            )}
            <button
              type="submit"
              disabled={status === "sending" || !email.trim()}
              className="btc-btn-primary inline-flex items-center gap-2 w-full justify-center disabled:opacity-60"
              data-testid="forgot-password-submit"
            >
              {status === "sending" ? "Sending…" : "Send reset instructions"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        <div className="mt-6 text-sm text-slate-500">
          <Link to="/login" className="underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
